/**
 * バックの調整 (v4.0.0)
 *
 * オーナー・責任者は、自動計算されたバック額を後から変えられる。
 * これは正当な業務で、実際に必要になる場面がある:
 *   - 計算の元になった伝票が間違っていた
 *   - キャストとの合意で個別に率を変えた
 *   - 罰金・ペナルティを引く（遅刻、無断欠勤）
 *   - 特別手当を足す（バースデー、指名本数、イベント）
 *   - 前月の計算漏れを今月で調整する
 *
 * 【設計方針】
 * 調整はできる。ただし、上書きではなく「調整」として積む。
 *   - 自動計算値（calculated）はそのまま残す
 *   - 調整は差分（delta）と理由で記録する
 *   - 支給額 = 自動計算値 + 調整の合計
 * こうすると「なぜこの額なのか」を後から必ず再現できる。
 * 上書きにすると、元がいくらだったかが消えて、
 * 本人からの問い合わせにも税務調査にも答えられなくなる。
 */

export type AdjustmentKind =
  | 'correction'   // 計算の訂正
  | 'penalty'      // 罰金・控除
  | 'bonus'        // 手当・特別支給
  | 'carryover'    // 前期からの繰越調整
  | 'agreement'    // 個別合意による変更
  | 'other';

export const ADJUSTMENT_LABELS: Record<AdjustmentKind, string> = {
  correction: '計算の訂正',
  penalty: '罰金・控除',
  bonus: '手当・特別支給',
  carryover: '前期からの繰越',
  agreement: '個別合意',
  other: 'その他',
};

export const ADJUSTMENT_DESCRIPTIONS: Record<AdjustmentKind, string> = {
  correction: '元になった伝票や配分が間違っていたので直す。',
  penalty: '遅刻・欠勤などの控除。就業規則にある場合のみ。',
  bonus: 'バースデー、指名本数、イベントなどの上乗せ。',
  carryover: '前の期の計算漏れ・過払いを今期で調整する。',
  agreement: '本人と合意のうえで率や額を変える。',
  other: '上のどれにも当てはまらない。理由の記入が必要。',
};

export interface BackAdjustment {
  id?: string;
  shopId: string;
  staffId: string;
  staffName: string;
  salesPeriodKey: string;
  /** 特定の会計に紐づく調整なら、その伝票 */
  checkoutId?: string;
  slipNumber?: string;

  kind: AdjustmentKind;
  /** 増減額。マイナスなら控除 */
  delta: number;
  /** 調整前の額（参照用。上書きはしない） */
  calculatedAmount: number;
  reason: string;

  adjustedBy: string;
  adjustedByName: string;
  adjustedByRole: string;
  adjustedAt: Date;

  /** 取り消す場合。記録は消さずに打ち消しを積む */
  reversedByAdjustmentId?: string;
  reversesAdjustmentId?: string;
}

export interface AdjustmentPolicy {
  /** 調整を実行できる役割 */
  allowedRoles: ('owner' | 'manager')[];
  /** 1件あたりの上限額。null = 制限なし */
  maxDeltaPerAdjustment: number | null;
  /** 自動計算値に対する変更率の上限（0.5 = ±50%）。null = 制限なし */
  maxRatioOfCalculated: number | null;
  /** 理由の記入を必須にするか */
  requireReason: boolean;
  /** 締め済みの期を調整できるか */
  allowAfterPeriodClose: boolean;
  /** キャスト本人に調整の内訳を見せるか */
  visibleToStaff: boolean;
}

export const DEFAULT_ADJUSTMENT_POLICY: AdjustmentPolicy = {
  allowedRoles: ['owner', 'manager'],
  maxDeltaPerAdjustment: null,
  maxRatioOfCalculated: null,
  requireReason: true,
  allowAfterPeriodClose: false,
  visibleToStaff: true,
};

export interface AdjustmentCheck {
  allowed: boolean;
  blockers: string[];
  warnings: string[];
}

export function checkAdjustmentAllowed(
  input: { delta: number; calculatedAmount: number; reason: string; kind: AdjustmentKind },
  ctx: { userRole: string; periodClosed?: boolean; alreadyPaidOut?: number; policy?: Partial<AdjustmentPolicy> }
): AdjustmentCheck {
  const policy: AdjustmentPolicy = { ...DEFAULT_ADJUSTMENT_POLICY, ...(ctx.policy ?? {}) };
  const blockers: string[] = [];
  const warnings: string[] = [];

  if (!policy.allowedRoles.includes(ctx.userRole as any)) {
    blockers.push('バックの調整はオーナー・責任者のみが実行できます。');
  }
  if (input.delta === 0) {
    blockers.push('増減額が 0 です。');
  }
  if (policy.requireReason && !input.reason.trim()) {
    blockers.push('理由の記入が必要です。');
  }
  if (policy.maxDeltaPerAdjustment !== null && Math.abs(input.delta) > policy.maxDeltaPerAdjustment) {
    blockers.push(
      `1件あたりの調整上限 ¥${policy.maxDeltaPerAdjustment.toLocaleString()} を超えています。`
    );
  }
  if (policy.maxRatioOfCalculated !== null && input.calculatedAmount > 0) {
    const ratio = Math.abs(input.delta) / input.calculatedAmount;
    if (ratio > policy.maxRatioOfCalculated) {
      blockers.push(
        `自動計算額の ${Math.round(policy.maxRatioOfCalculated * 100)}% を超える調整です` +
        `（今回 ${Math.round(ratio * 100)}%）。`
      );
    }
  }
  if (ctx.periodClosed && !policy.allowAfterPeriodClose) {
    blockers.push('この給与期間は締め済みです。締め後の調整は店舗設定で許可できます。');
  }

  const after = input.calculatedAmount + input.delta;
  if (after < 0) {
    warnings.push(
      `調整後の支給額がマイナス（¥${after.toLocaleString()}）になります。` +
      '控除しきれないぶんは次期へ繰り越すか、別途精算が必要です。'
    );
  }
  if (ctx.alreadyPaidOut != null && after < ctx.alreadyPaidOut) {
    warnings.push(
      `日払いで ¥${ctx.alreadyPaidOut.toLocaleString()} をすでに渡しています。` +
      `調整後の支給額 ¥${after.toLocaleString()} を上回るため、差額の回収が必要になります。`
    );
  }
  if (input.kind === 'penalty') {
    warnings.push(
      '罰金・控除は、就業規則や雇用契約に定めがある範囲でのみ有効です。' +
      '労働基準法では減給の制裁に上限（1回で平均賃金1日分の半額、総額で賃金支払期の10分の1）があります。'
    );
  }

  return { allowed: blockers.length === 0, blockers, warnings };
}

export interface AdjustmentSummary {
  calculated: number;
  adjustmentTotal: number;
  payable: number;
  breakdown: { kind: AdjustmentKind; label: string; total: number; count: number }[];
}

/**
 * 自動計算値と調整から、実際の支給額を組み立てる。
 * 明細画面と給与画面はこの結果だけを使う。
 */
export function summarizeAdjustments(
  calculated: number,
  adjustments: BackAdjustment[]
): AdjustmentSummary {
  const live = adjustments.filter((a) => !a.reversedByAdjustmentId);
  const byKind = new Map<AdjustmentKind, { total: number; count: number }>();
  let total = 0;
  for (const a of live) {
    total += a.delta;
    const cur = byKind.get(a.kind) ?? { total: 0, count: 0 };
    cur.total += a.delta;
    cur.count += 1;
    byKind.set(a.kind, cur);
  }
  return {
    calculated,
    adjustmentTotal: total,
    payable: calculated + total,
    breakdown: Array.from(byKind.entries()).map(([kind, v]) => ({
      kind,
      label: ADJUSTMENT_LABELS[kind],
      total: v.total,
      count: v.count,
    })),
  };
}

/** 明細に出す「なぜこの額なのか」の説明 */
export function describePayable(s: AdjustmentSummary): string {
  if (s.breakdown.length === 0) {
    return `自動計算 ¥${s.calculated.toLocaleString()}（調整なし）`;
  }
  const parts = s.breakdown.map(
    (b) => `${b.label} ${b.total >= 0 ? '+' : '−'}¥${Math.abs(b.total).toLocaleString()}`
  );
  return `自動計算 ¥${s.calculated.toLocaleString()} ${parts.join(' ')} = ¥${s.payable.toLocaleString()}`;
}
